// DetailHeader — Detail Panel 상단 헤더 컴포넌트 (U42 + U43 이름 인라인 편집).
//
// 표시 요소:
//   - 항목 이름 (h2, truncate) — U43: InlineEdit로 클릭 편집 가능
//   - 이름 복사 버튼 (카드의 "이름 복사"를 여기로 이동, 2026-10-03)
//   - 타입 아이콘 (lucide-react)
//   - 핀 토글 버튼 (★ / ☆)
//   - 수정(연필) — 수정 폼 토글 / 삭제(휴지통) — 휴지통 이동 (2026-10-02, 기존 ⋯ placeholder 대체)
//   - X 닫기 버튼

import { Star, X, Pencil, Trash2 } from 'lucide-react';
import { Icon } from '../Icon';
import { CopyButton } from '../CopyButton';
import { InlineEdit } from '../../ui/InlineEdit';
import { TYPE_ICON_MAP, TYPE_LABEL } from './typeMap';
import type { ItemType } from '../../api/items';

interface DetailHeaderProps {
  /** 항목 이름 */
  name: string;
  /** 항목 타입 */
  type: ItemType | string;
  /** 핀 여부 (0 or 1) */
  pinned: 0 | 1;
  /** 핀 토글 핸들러 */
  onPinToggle: () => void;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 이름 저장 핸들러 (U43 인라인 편집) */
  onSaveName?: (newName: string) => Promise<void>;
  /** 수정 모드 토글 핸들러 */
  onEdit?: () => void;
  /** 수정 모드 여부 (버튼 활성 표시) */
  editing?: boolean;
  /** 삭제 핸들러 */
  onDelete?: () => void;
}

/**
 * Detail Panel 헤더.
 * 항목 이름·타입 아이콘·핀 토글·닫기 버튼을 한 줄로 렌더한다.
 * onSaveName이 주어지면 이름 클릭 → InlineEdit 전환.
 */
export function DetailHeader({
  name,
  type,
  pinned,
  onPinToggle,
  onClose,
  onSaveName,
  onEdit,
  editing = false,
  onDelete,
}: DetailHeaderProps) {
  const TypeIcon = TYPE_ICON_MAP[type as ItemType] ?? TYPE_ICON_MAP['note'];
  const typeLabel = TYPE_LABEL[type as ItemType] ?? type;

  return (
    <header className="detail-panel__header">
      {/* 타입 아이콘 + 이름 */}
      <div className="detail-panel__title-row">
        <span className="detail-panel__type-icon" aria-label={typeLabel} title={typeLabel}>
          <Icon icon={TypeIcon} size="md" />
        </span>
        {onSaveName ? (
          // 인라인 편집 모드 (U43)
          <InlineEdit
            value={name}
            onSave={onSaveName}
            type="text"
            placeholder="항목 이름"
            ariaLabel="항목 이름 편집"
            className="detail-panel__title-edit"
            validate={(v) => {
              if (!v.trim()) return '이름을 입력하세요.';
              return true;
            }}
          />
        ) : (
          // 편집 불가 표시 전용
          <h2 className="detail-panel__title" title={name}>
            {name}
          </h2>
        )}
        <CopyButton value={name} label="복사" className="detail-panel__name-copy" />
      </div>

      {/* 액션 버튼 그룹 */}
      <div className="detail-panel__actions">
        {/* 핀 토글 */}
        <button
          type="button"
          className={`detail-panel__action-btn${pinned === 1 ? ' detail-panel__action-btn--active' : ''}`}
          onClick={onPinToggle}
          aria-label={pinned === 1 ? '즐겨찾기 해제' : '즐겨찾기 추가'}
          title={pinned === 1 ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        >
          <Icon
            icon={Star}
            size="md"
            style={pinned === 1 ? { fill: 'var(--warning)', color: 'var(--warning)' } : undefined}
          />
        </button>

        {/* 수정 — 전체 필드 수정 폼 토글 */}
        {onEdit && (
          <button
            type="button"
            className={`detail-panel__action-btn${editing ? ' detail-panel__action-btn--on' : ''}`}
            onClick={onEdit}
            aria-label={editing ? '수정 취소' : '수정'}
            aria-pressed={editing}
            title={editing ? '수정 취소' : '수정'}
          >
            <Icon icon={Pencil} size="md" />
          </button>
        )}

        {/* 삭제 — 휴지통 이동 (Undo 가능) */}
        {onDelete && (
          <button
            type="button"
            className="detail-panel__action-btn detail-panel__delete-btn"
            onClick={onDelete}
            aria-label="삭제"
            title="삭제 (휴지통으로 이동)"
          >
            <Icon icon={Trash2} size="md" />
          </button>
        )}

        <span className="detail-panel__action-sep" aria-hidden="true" />

        {/* 닫기 */}
        <button
          type="button"
          className="detail-panel__action-btn detail-panel__close-btn"
          onClick={onClose}
          aria-label="닫기"
          title="닫기 (Esc)"
        >
          <Icon icon={X} size="md" />
        </button>
      </div>
    </header>
  );
}
